//@flow
import { setAlertData, stopLoading } from "Redux/Screen/Action";
import axios from "axios";
import { store } from "Redux/Store";
import {
  CheckPaginateNumber,
  CheckStringEmpty,
  Logout,
  getPaginationPage,
} from "Utils/common";
export const authTokenKey = "Authorization";

/**
 * Where a forced-logout reason is parked so it survives the redirect.
 *
 * Logout() navigates with window.location, which tears down the React app and
 * the Redux store, so an alert dispatched at the point of failure never gets
 * rendered. sessionStorage is used rather than localStorage because Logout()
 * and the login screen both call localStorage.clear().
 */
export const LOGOUT_REASON_KEY = "cm-logout-reason";

const apiBaseUrls = {
  prod: process.env.REACT_APP_BASE_URL,
  dev: process.env.REACT_APP_BASE_URL,
  stage: process.env.REACT_APP_BASE_URL,
}; /*  */

const apiBaseUrlsAuth = {
  prod: process.env.REACT_APP_BASE_AUTH_URL,
  dev: process.env.REACT_APP_BASE_AUTH_URL,
  stage: process.env.REACT_APP_BASE_AUTH_URL,
};

const apiBaseUrlsRedox = {
  prod: process.env.REACT_APP_BASE_REDOX_URL,
  dev: process.env.REACT_APP_BASE_REDOX_URL,
  stage: process.env.REACT_APP_BASE_REDOX_URL,
};

const react_app_env = {
  prod: "production",
  dev: "development",
};

let baseUrl = apiBaseUrls.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrl = apiBaseUrls.prod;
}

let baseUrlAuth = apiBaseUrlsAuth.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrlAuth = apiBaseUrlsAuth.prod;
}

let baseUrlRedox = apiBaseUrlsRedox.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrlRedox = apiBaseUrlsRedox.prod;
}

export const get = (request: any) => {
  return commonFetch({ method: "get", ...request });
};

export const post = (request: any) => {
  return commonFetch({ method: "post", ...request });
};

export const patch = (request: any) => {
  return commonFetch({ method: "patch", ...request });
};

export const put = (request: any) => {
  return commonFetch({ method: "put", ...request });
};

export const deletee = (request: any) => {
  return commonFetch({ method: "delete", ...request });
};

const inFlightRequests = new Map<string, AbortController>();

const commonFetch = (request: any) => {
  const dispatch = store.dispatch;
  try {
    const {
      subUrl,
      method,
      data = {},
      params = {},
      headers = {},
      isAuth,
      isRedox,
      excel,
      dedupe,
      dedupeKey,
    } = request;

    let url;
    if (isAuth) url = `${baseUrlAuth}/auth/${subUrl}`;
    else if (isRedox) url = `${baseUrl}/redox/${subUrl}`;
    else url = `${baseUrl}/api/${subUrl}`;

    const passedHeaders = headers;
    const commonHeaders = getCommonHeaders(excel);
    const responseType = excel ? "blob" : "json";

    const key = dedupeKey || (dedupe ? subUrl : undefined);
    let controller: AbortController | undefined;
    if (key) {
      inFlightRequests.get(key)?.abort();
      controller = new AbortController();
      inFlightRequests.set(key, controller);
    }
    const clearInFlight = () => {
      if (key && inFlightRequests.get(key) === controller) {
        inFlightRequests.delete(key);
      }
    };

    return axios({
      method,
      url,
      // data: { data },
      responseType,
      data,
      withCredentials: false,
      headers: excel
        ? { ...commonHeaders }
        : { ...commonHeaders, ...passedHeaders },
      params,
      signal: controller?.signal,
    })
      .then((response) => {
        clearInFlight();
        if (response?.data?.status == 1) {
          return {
            success: true,
            data: response?.data?.data,
            criterion: {
              limit: CheckPaginateNumber(response?.data?.criterion?.limit),
              page: getPaginationPage(
                CheckPaginateNumber(response?.data?.criterion?.offset),
                CheckPaginateNumber(response?.data?.criterion?.limit)
              ),
              total: CheckPaginateNumber(response?.data?.criterion?.total),
            },
            alert: {
              alertMessage: CheckStringEmpty(
                response?.data?.msg || response?.data?.error || "Save Changes"
              ),
              openAlert: true,
              alertVariant: "success",
            },
          };
        } else if (excel) {
          return { success: true, data: response?.data };
        } else if (response?.data?.status == 2) {
          dispatch(
            setAlertData({
              alertMessage: CheckStringEmpty(
                response?.data?.error ||
                response?.data?.msg ||
                "Something went wrong!"
              ),
              openAlert: true,
              alertVariant: "warning",
            })
          );
          dispatch(stopLoading());
          return {
            success: false,
          };
        }
        else {
          dispatch(
            setAlertData({
              alertMessage: CheckStringEmpty(
                response?.data?.error ||
                response?.data?.msg ||
                "Something went wrong!"
              ),
              openAlert: true,
              alertVariant: "error",
            })
          );
          dispatch(stopLoading());
          return {
            success: false,
          };
        }
      })
      .catch((error) => {
        clearInFlight();
        if (axios.isCancel(error)) {
          return {
            success: false,
            canceled: true,
          };
        }
        const apiErrMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.msg;
        if (error?.response?.status == 400) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Unable to perform this action. Please try after sometime.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 401) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Session has expired. Please log in again.",
              openAlert: true,
              alertVariant: "error",
            })
          );
          Logout();
        } else if (error?.response?.status == 403) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "You are not authorized to perform this action.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 404) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Resource not found. Please try after sometime.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 409) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "User login to another system with these credentials.",
              openAlert: true,
              alertVariant: "error",
            })
          );
          try {
            sessionStorage.setItem(
              LOGOUT_REASON_KEY,
              apiErrMsg ||
              "User login to another system with these credentials."
            );
          } catch {
            /* sessionStorage unavailable — fall back to the transient alert */
          }
          Logout();
        } else if (error?.response?.status == 500) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Something went wrong. Please login after sometime",
              openAlert: true,
              alertVariant: "error",
            })
          );
          Logout();
        } else if (error?.response?.status == 503) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Service Unavailable. Please login after sometime",
              openAlert: true,
              alertVariant: "error",
            })
          );
          Logout();
        } else {
          dispatch(
            setAlertData({
              alertMessage:
                "Something went wrong. Please login after sometime.",
              openAlert: true,
              alertVariant: "error",
            })
          );
          Logout();
        }
        dispatch(stopLoading());
        return {
          success: false,
        };
      });
  } catch (err) {
    dispatch(
      setAlertData({
        openAlert: true,
        alertMessage: JSON.stringify(err),
        alertVariant: "error",
      })
    );
    dispatch(stopLoading());
    return {
      success: false,
    };
  }
};

const getCommonHeaders = (excel?: boolean) => {
  const localToken = localStorage.getItem("cm-token") || "";
  const tokenStorage = localToken ? JSON.parse(localToken) : "";

  const headerObj: any = {
    "Content-Type":
      // excel ? "application/vnd.ms-excel" :
      "application/json",
    // Accept: excel
    //   ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    //   : "",
    "Access-Control-Allow-Origin": true,
    [authTokenKey]: tokenStorage ? `Bearer ${tokenStorage}` : "",
  };
  const headerObjForExcel: any = {
    [authTokenKey]: tokenStorage ? `Bearer ${tokenStorage}` : "",
  };
  return excel ? headerObjForExcel : headerObj;
};
